import React, { useState } from 'react';
import { ApiRequest } from '@/utils/axios';
import { modalStore } from '@/stores/modal.store';
import { notify } from '@/utils/notify';
import AlertIcon from '@/components/Notifications/AlertIcon';
import SuccessIcon from '@/components/Notifications/SuccessIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import { ITicket } from '@/utils/types/ITicket';

interface Props {
    load: () => Promise<ITicket[]>;
    onCreated?: (ticket: ITicket) => void;
}

const CATEGORIES = [
    { value: 'General', label: 'General question' },
    { value: 'Deposit', label: 'Deposit / Withdraw' },
    { value: 'Skins', label: 'Skin not received' },
    { value: 'Account', label: 'Account access' },
    { value: 'Bug', label: 'Bug report' },
];

const NewTicket = ({ load, onCreated }: Props) => {
    const { toggleModal, setData } = modalStore((state) => state);

    const [category, setCategory] = useState<string>(CATEGORIES[0].value);
    const [subject, setSubject] = useState<string>('');
    const [body, setBody] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);

    async function submit() {
        if (!subject.trim() || !body.trim()) {
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper
                    title={'Missing fields'}
                    text={'Subject and message are both required.'}
                />
            );
            return;
        }

        setSubmitting(true);
        try {
            const {
                data: { response },
            } = await ApiRequest.post('/support/tickets/create');
            const ticketId = response?.id;

            if (ticketId) {
                const categoryLabel =
                    CATEGORIES.find((c) => c.value === category)?.label || category;
                const composed = `[${categoryLabel}]\n${subject.trim()}\n\n${body.trim()}`;
                await ApiRequest.post('/support/messages/send', {
                    ticketId,
                    message: composed,
                });
            }

            const tickets = await load();
            const created = tickets.find((t) => t.id === ticketId);

            notify(
                'success',
                <SuccessIcon />,
                <NotifyWrapper title={'Ticket created'} text={`Ticket #${ticketId} opened.`} />
            );

            toggleModal(false);
            setData({});
            if (created && onCreated) onCreated(created);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="flex flex-col gap-3.5 pt-1 w-[520px] max-w-full">
            <div>
                <div className="text-[12.5px] font-semibold text-text_2 mb-1.5">Category</div>
                <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className={
                        'w-full bg-layer_2 border border-stroke rounded-fl px-3.5 py-3 ' +
                        'text-text_main text-[13px] outline-none appearance-none cursor-pointer ' +
                        'hover:border-stroke_strong hover:bg-layer_3 transition-colors'
                    }
                    style={{
                        backgroundImage:
                            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23ccc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        paddingRight: '36px',
                    }}
                >
                    {CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value} style={{ background: '#1c1e25' }}>
                            {c.label}
                        </option>
                    ))}
                </select>
            </div>

            <div>
                <div className="text-[12.5px] font-semibold text-text_2 mb-1.5">Subject</div>
                <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Brief summary of your issue"
                    className={
                        'w-full bg-layer_2 border border-stroke rounded-fl px-3.5 py-3 ' +
                        'text-text_main text-[13px] outline-none placeholder:text-text_4 ' +
                        'focus:border-accent focus:bg-layer_3 transition-colors'
                    }
                />
            </div>

            <div>
                <div className="text-[12.5px] font-semibold text-text_2 mb-1.5">Message</div>
                <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Describe what happened, when, and any relevant IDs"
                    className={
                        'w-full min-h-[120px] resize-y bg-layer_2 border border-stroke rounded-fl px-3.5 py-3 ' +
                        'text-text_main text-[13px] leading-[1.5] outline-none placeholder:text-text_4 ' +
                        'focus:border-accent focus:bg-layer_3 transition-colors'
                    }
                />
            </div>

            <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className={
                    'mt-1 py-3.5 rounded-fl text-white text-[13px] font-bold tracking-[0.06em] border-0 cursor-pointer ' +
                    'hover:-translate-y-[1px] hover:brightness-[1.06] disabled:opacity-45 disabled:cursor-not-allowed disabled:shadow-none ' +
                    'transition-[transform,filter,opacity] duration-150'
                }
                style={{
                    background: 'linear-gradient(180deg, #3acdb1, #2bb89b)',
                    boxShadow:
                        '0 10px 24px rgba(43,184,155,0.34), inset 0 1px 0 rgba(255,255,255,0.30)',
                }}
            >
                CREATE TICKET
            </button>
        </div>
    );
};

export default NewTicket;
